import { useEffect, useState } from "react";
import { DrawerForm } from "../../../components/drawers/DrawerForm";
import Indicator from "../../../components/base/Indicator";
import Icon from "../../../components/base/Icon";
import TextBox from "../../../components/form/TextBox";
import ToggleSwitch from "../../../components/form/ToggleSwitch";
import { toShortDateWithoutTime, useHub } from "../../../core/Global";
import { toastError, toastSuccess } from "../../../components/base/Toast";
import MigrationRow from "../models/MigrationRow";
import CustomPlanFields, { ADDONS, AddonChoiceRow, AddonKey, AddonPrices, CustomPlanForm, customMonthly, customTerms } from "./CustomPlanFields";


const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 })

const priceText = (v: number) => v <= 0 ? "Free" : money.format(v) + "/month"


// Moving one customer off the old subscription onto a plan, now rather than at their renewal.
//
// The document's migration rule: nothing is charged today and the renewal date does not move.
// The plan takes over the rest of the period they already paid for, and its own price is
// charged at that renewal. WhatsApp, Telegram and Masked Job Calls are switched on with it.
//
// What they pay today is laid out first, because a good part of the customers do not pay the
// list price. The plan can keep a price set by hand -- carried over, changed, or zero for an
// account that should stay free.
//
// Or the plan can be made for them alone ("Custom"): every allowance, every rate and every add-on
// -- included, sold to them at its price, or left off -- set here. The same rule applies: nothing
// is charged today, and the price and the add-ons sold on it are first charged at the renewal.
interface SessionHolder {
    name?: string
    username?: string
    isTech?: boolean
    isOwner?: boolean
    sessions: number
    platforms: string[]
    lastActiveAt?: string
}


export default function MigrateDrawer(props: {
    row: MigrationRow,
    onClose: () => void,
    onMoved: () => void
}) {

    const hub = useHub()
    const r = props.row

    const [plans, plansSet] = useState<any[]>([])
    const [loading, loadingSet] = useState<boolean>(true)
    const [planCode, planCodeSet] = useState<string>("pro")

    // A customer already on a price of their own keeps it unless told otherwise.
    const [custom, customSet] = useState<boolean>(!!r.hasCustomCharge)
    const [customPrice, customPriceSet] = useState<string>(r.hasCustomCharge ? String(r.subscriptionCharge) : "")

    // The custom plan: starts from Pro's figures, with what they pay and the seats they hold today.
    const [customForm, customFormSet] = useState<CustomPlanForm>()
    const [addonPrices, addonPricesSet] = useState<AddonPrices>()

    // Everybody on the account holding a session: a move signs them all out, the owner too
    // (never system admins), so they sign in again and are counted against the new plan.
    const [holders, holdersSet] = useState<SessionHolder[]>([])

    // On a catalog plan: the add-ons it does not include that are sold to the customer with it.
    const [paidAddons, paidAddonsSet] = useState<AddonKey[]>([])

    // A stopped account: how many days from today before the plan is first charged.
    const [firstCharge, firstChargeSet] = useState<string>("0")

    useEffect(() => {
        let waiting = 3
        const done = () => { if (--waiting === 0) loadingSet(false) }

        hub.Get("/system/migration/plans", res => {
            if (res.success) plansSet(res.data ?? [])
            done()
        }, done)

        hub.Get("/system/migration/sessions/" + r.userId, res => {
            if (res.success) holdersSet(res.data ?? [])
            done()
        }, done)

        hub.Get("/system/migration/custom", res => {
            if (res.success) {
                const t = res.data.terms
                addonPricesSet(res.data.prices)
                customFormSet({
                    price: String(r.subscriptionCharge ?? t.price),
                    users: String(Math.max(r.users ?? 0, 1)),
                    messages: String(t.messages),
                    minutes: String(t.minutes),
                    numbers: String(t.numbers),
                    messageRate: String(t.messageRate),
                    minuteRate: String(t.minuteRate),
                    numberPrice: String(t.numberPrice),
                    whatsApp: t.whatsApp,
                    telegram: t.telegram,
                    maskedJobCalls: t.maskedJobCalls
                })
            }
            done()
        }, done)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const isCustom = planCode === "custom"
    const plan = isCustom
        ? { code: "custom", name: "Custom", users: Number.parseInt(customForm?.users ?? "0") || 0 }
        : plans.find(p => p.code === planCode)
    // Free costs nothing: no custom price, and nothing for the renewal to charge for the plan.
    const isFree = !isCustom && !!plan && plan.price <= 0
    // A stopped account has no paid period left: the plan starts today and is first charged
    // after the days chosen -- today meaning the renewal engine's next pass.
    const firstDays = Number.parseInt(firstCharge.replace(/,/g, ""))
    const firstValid = !r.isInactive || (!Number.isNaN(firstDays) && firstDays >= 0 && firstDays <= 90)
    const renews = r.isInactive
        ? (firstValid && firstDays > 0 ? toShortDateWithoutTime(new Date(Date.now() + firstDays * 86400000)) : "today")
        : toShortDateWithoutTime(new Date(r.endsAt))
    const fewerUsers = !!plan && plan.users < r.users

    const addons = r.paidAddons ?? []
    const seatsTotal = (r.seatPackages ?? 0) * (r.seatPackagePrice ?? 0)
    const todayTotal = (r.subscriptionCharge ?? 0) + seatsTotal + addons.reduce((s, a) => s + a.price, 0)

    const parsedCustom = Number.parseFloat(customPrice.replace(/,/g, ""))
    const customValid = !custom || isFree || (!Number.isNaN(parsedCustom) && parsedCustom >= 0)
    // Sold with a catalog plan: only what that plan does not already include.
    const soldWith = isCustom || !plan ? [] : paidAddons.filter(k => !plan[k])
    const soldTotal = soldWith.reduce((s, k) => s + (addonPrices?.[k] ?? 0), 0)

    const basePrice = custom && !isFree ? (Number.isNaN(parsedCustom) ? undefined : parsedCustom) : plan?.price
    const newPrice = isCustom
        ? (customForm ? customMonthly(customForm, addonPrices) : undefined)
        : basePrice === undefined ? undefined : basePrice + soldTotal

    const move = (callback: any) => {
        hub.Post("/system/migration/move", {
            userId: r.userId,
            planCode,
            customPrice: !isCustom && !isFree && custom ? parsedCustom : null,
            paidAddons: soldWith,
            firstChargeInDays: r.isInactive ? firstDays : null,
            custom: isCustom && customForm ? customTerms(customForm) : null
        }, res => {
            callback(res)
            if (res.success) {
                const out = res.data?.signedOut ?? 0
                toastSuccess(`${r.name} is on ${res.data?.planName} now, renewing ${renews}.` +
                    (out > 0 ? ` ${out} session${out === 1 ? "" : "s"} signed out.` : ""))
                props.onMoved()
            }
            else toastError(res.message ?? "Couldn't move this customer.")
        }, () => callback({ success: false }))
    }

    return (
        <DrawerForm id="drawer-migrate-customer"
            title={"Move " + (r.name ?? r.username)}
            titleIcon="arrow-right-arrow-left"
            subtitle="From the old subscription onto a new plan"
            size="medium"
            buttonText={plan ? "Move to " + plan.name : "Move"}
            confirmDisabled={!plan || !firstValid || (!isCustom && !customValid) || (isCustom && !customForm)}
            validator={!isCustom && !isFree && custom ? [{ name: "customPrice", required: true }] : []}
            onClose={props.onClose}
            onSubmit={(body, callback) => move(callback)}>

            {
                loading ?
                    <div className="py-20"><Indicator /></div>
                    :
                    <div>
                        {
                            r.isInactive &&
                            <div className="migration-warn mb-6">
                                <Icon icon="rotate-right" type="solid" size={13} />
                                <div>
                                    Their old subscription has stopped ({r.status?.toLowerCase()}). Moving them brings the account back today:
                                    unlocked, on the plan chosen below, and charged for it after the days you set.
                                </div>
                            </div>
                        }

                        <div className="fw-bold mb-3">{r.isInactive ? "What they paid before" : "What they pay today"}</div>
                        <div className="migration-summary mb-6">
                            <div className="migration-summary-row">
                                <span>
                                    Subscription
                                    {r.hasCustomCharge && <span className="badge badge-light-warning ms-2">custom</span>}
                                </span>
                                <b>{priceText(r.subscriptionCharge ?? 0)}</b>
                            </div>
                            {
                                (r.seatPackages ?? 0) > 0 &&
                                <div className="migration-summary-row">
                                    <span>Seat packages</span>
                                    <b>{r.seatPackages} × {money.format(r.seatPackagePrice ?? 0)}</b>
                                </div>
                            }
                            {
                                addons.map(a =>
                                    <div key={a.name} className="migration-summary-row">
                                        <span>{a.name}</span>
                                        <b>{money.format(a.price)}/month</b>
                                    </div>
                                )
                            }
                            <div className="migration-summary-row">
                                <span>In all</span>
                                <b>{money.format(todayTotal)}/month</b>
                            </div>
                            <div className="migration-summary-row">
                                <span>Last paid</span>
                                <b>{money.format(r.price)}</b>
                            </div>
                        </div>

                        <div className="fw-bold mb-3">Which plan?</div>
                        <div className="migration-plans mb-6">
                            {
                                plans.map(p =>
                                    <button key={p.code} type="button"
                                        className={"migration-plan" + (p.code === planCode ? " is-selected" : "")}
                                        onClick={e => { e.preventDefault(); planCodeSet(p.code); }}>
                                        <div className="migration-plan-name">{p.name}</div>
                                        <div className="migration-plan-price">
                                            {p.price > 0 ? <>{money.format(p.price)}<span>/month</span></> : "Free"}
                                        </div>
                                        <div className="migration-plan-users">{p.users} users at once</div>
                                    </button>
                                )
                            }
                            {
                                customForm &&
                                <button type="button"
                                    className={"migration-plan" + (isCustom ? " is-selected" : "")}
                                    onClick={e => { e.preventDefault(); planCodeSet("custom"); }}>
                                    <div className="migration-plan-name">Custom</div>
                                    <div className="migration-plan-price"><Icon icon="sliders" type="solid" size={14} /></div>
                                    <div className="migration-plan-users">Set every figure</div>
                                </button>
                            }
                        </div>

                        {
                            isCustom && customForm &&
                            <div className="mb-6">
                                <CustomPlanFields form={customForm} prices={addonPrices} onChange={customFormSet} />
                            </div>
                        }

                        {
                            !isCustom && plan &&
                            <div className="mb-6">
                                <div className="fw-bold mb-3">Add-ons</div>
                                <div className="migration-custom migration-custom-addons">
                                    {
                                        ADDONS.map(a =>
                                            plan[a.key] ?
                                                <div key={a.key} className="migration-custom-addon">
                                                    <div className="fw-semibold">{a.label}</div>
                                                    <span className="badge badge-light-primary">Included in {plan.name}</span>
                                                </div>
                                                :
                                                <AddonChoiceRow key={a.key} label={a.label} price={addonPrices?.[a.key]}
                                                    choices={["paid", "off"]}
                                                    value={paidAddons.includes(a.key) ? "paid" : "off"}
                                                    onChange={v => paidAddonsSet(list => v === "paid"
                                                        ? [...list.filter(k => k !== a.key), a.key]
                                                        : list.filter(k => k !== a.key))} />
                                        )
                                    }
                                </div>
                            </div>
                        }

                        <div className={"mb-6" + (isCustom || isFree ? " d-none" : "")}>
                            <ToggleSwitch name="useCustomPrice" label="Custom price on the new plan"
                                checked={custom}
                                onChange={on => customSet(on)} />
                            {
                                custom &&
                                <div className="mt-4">
                                    <TextBox name="customPrice" label="Monthly price (0 for a free account)" labelUp block money
                                        icon="dollar-sign" defaultValue={customPrice}
                                        onChange={e => customPriceSet(e.target.value)} />
                                    <div className="text-gray-500 fs-8 mt-n2">
                                        Kept for this customer: a change to {plan?.name ?? "the plan"}'s catalog price does not touch it.
                                    </div>
                                </div>
                            }
                        </div>

                        {
                            r.isInactive &&
                            <div className="mb-6">
                                <TextBox name="firstChargeInDays" label="Days before the first charge (0 = charge now)" labelUp block numeric
                                    value={firstCharge} onTextChanges={v => firstChargeSet(v)} />
                                <div className="text-gray-500 fs-8 mt-n2">
                                    0 to 90. With 0 the renewal engine charges the plan on its next pass, within a quarter of an hour.
                                </div>
                            </div>
                        }

                        <div className="migration-summary mb-6">
                            <div className="migration-summary-row">
                                <span>Charged now</span>
                                <b>Nothing</b>
                            </div>
                            <div className="migration-summary-row">
                                <span>{r.isInactive ? "First charge" : "Renewal date"}</span>
                                <b>{r.isInactive ? (renews === "today" ? "Today, by the renewal engine" : renews) : renews + " — unchanged"}</b>
                            </div>
                            <div className="migration-summary-row">
                                <span>From then</span>
                                <b>
                                    {newPrice === undefined ? "—" : priceText(newPrice)}
                                    {(custom || isCustom) && newPrice !== undefined && <span className="badge badge-light-warning ms-2">custom</span>}
                                </b>
                            </div>
                            <div className="migration-summary-row">
                                <span>Users at once</span>
                                <b className={fewerUsers ? "text-danger" : ""}>
                                    {r.users}
                                    <Icon icon="arrow-right" type="solid" size={10} className="mx-2 text-gray-500" />
                                    {plan?.users ?? "—"}
                                </b>
                            </div>
                        </div>

                        {
                            fewerUsers &&
                            <div className="migration-warn mb-6">
                                <Icon icon="triangle-exclamation" type="solid" size={13} />
                                <div>
                                    They hold {r.users} seats today and {plan.name} lets {plan.users} be signed in at once.
                                    They can buy extra users afterwards, or move them onto a bigger plan.
                                </div>
                            </div>
                        }

                        <div className="fw-bold mb-3">
                            Signed in now
                            <span className="text-gray-500 fw-semibold ms-2">
                                — {holders.reduce((n, h) => n + h.sessions, 0)} session(s) signed out by the move
                            </span>
                        </div>
                        <div className="migration-summary mb-6">
                            {
                                holders.length === 0 ?
                                    <div className="migration-summary-row"><span>Nobody on this account holds a session.</span></div>
                                    :
                                    holders.map(h =>
                                        <div key={h.username} className="migration-summary-row">
                                            <span>
                                                <b className="text-gray-800">{h.name ?? h.username}</b>
                                                {h.isOwner && <span className="badge badge-light-primary ms-2">owner</span>}
                                                {h.isTech && <span className="badge badge-light ms-2">tech</span>}
                                                <span className="text-gray-500 fs-8 ms-2">{h.platforms.join(", ")}</span>
                                            </span>
                                            <b>{h.sessions} session{h.sessions === 1 ? "" : "s"}</b>
                                        </div>
                                    )
                            }
                        </div>

                        <ul className="migration-notes">
                            {
                                r.isInactive ?
                                    <li><Icon icon="check" type="solid" size={10} />The plan starts today. If the first charge does not go through, the account moves to Free.</li>
                                    : <li><Icon icon="check" type="solid" size={10} />The rest of the period they paid for carries over.</li>
                            }
                            {
                                isCustom ?
                                    <>
                                        <li><Icon icon="check" type="solid" size={10} />Included add-ons are switched on with the plan; paid ones are switched on now and first charged on {renews}.</li>
                                        <li><Icon icon="check" type="solid" size={10} />Messages and minutes beyond what is included come out of the wallet, at the rates above.</li>
                                        <li><Icon icon="check" type="solid" size={10} />The plan's price is first charged on {renews}, by the renewal. The customer cannot change this plan themselves.</li>
                                    </>
                                    :
                                    isFree ?
                                    <>
                                        <li><Icon icon="check" type="solid" size={10} />Free includes no add-ons. One they already pay for keeps running on its own date until they cancel it.</li>
                                        {
                                            soldWith.length > 0 &&
                                            <li><Icon icon="check" type="solid" size={10} />{soldWith.map(k => ADDONS.find(a => a.key === k)?.label).join(" and ")} {soldWith.length === 1 ? "is" : "are"} switched on now and charged on {renews}.</li>
                                        }
                                        <li><Icon icon="check" type="solid" size={10} />Nothing is ever charged for the plan. Messages and minutes come out of the wallet, and each number beyond the one included costs {money.format(plan?.numberPrice ?? 0)} a month.</li>
                                    </>
                                    :
                                    <>
                                        <li><Icon icon="check" type="solid" size={10} />The add-ons {plan?.name ?? "the plan"} includes are switched on with it{addons.length > 0 ? `; ${addons.map(a => a.name).join(" and ")} stop${addons.length === 1 ? "s" : ""} being billed separately` : ""}.</li>
                                        {
                                            soldWith.length > 0 &&
                                            <li><Icon icon="check" type="solid" size={10} />{soldWith.map(k => ADDONS.find(a => a.key === k)?.label).join(" and ")} {soldWith.length === 1 ? "is" : "are"} switched on now and first charged on {renews}.</li>
                                        }
                                        <li><Icon icon="check" type="solid" size={10} />The plan's price is first charged on {renews}, by the renewal.</li>
                                    </>
                            }
                            <li><Icon icon="circle-info" type="solid" size={10} />Everybody on the account is signed out, the owner too (never system admins), and signs in again, so the new plan's users are counted from the start.</li>
                            <li><Icon icon="circle-info" type="solid" size={10} />The customer is not notified by this.</li>
                        </ul>
                    </div>
            }

        </DrawerForm>
    )
}
